import { IToast, Id, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { contactFilterService } from "../api/contactFilterService";
import { contactUpdateInquiryService } from "../api/contactUpdateInquiryService";
import { ArchiveByTenantRequest } from "../types/archive";

export type ITenantArchiveService = {
	archive(id: Id, request: ArchiveByTenantRequest): Promise<AxiosResponse<void>>;
	restore(id: Id, request: ArchiveByTenantRequest): Promise<AxiosResponse<void>>;
	indexArchivedKey: Id[];
	indexKey: Id[];
	getTenantsKey: (id: Id) => string[];
	getArchivedTenantsKey: (id: Id) => string[];
	optionsKey: Id[];
};

export const useArchiveAction = (service: ITenantArchiveService) => {
	const { t } = useTranslation();
	return useBaseAction(service, service.archive, {
		body: t("Successfully archived"),
		title: t("Archived"),
	});
};

export const useRestoreAction = (service: ITenantArchiveService) => {
	const { t } = useTranslation();
	return useBaseAction(service, service.restore, {
		body: t("Successfully restored"),
		title: t("Restored"),
	});
};

const useBaseAction = (
	service: ITenantArchiveService,
	action: (id: Id, request: ArchiveByTenantRequest) => Promise<AxiosResponse<void>>,
	toast: Pick<IToast, "body" | "title">,
) => {
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	return async (id: Id, request: ArchiveByTenantRequest) => {
		const response = await action(id, request);

		if (isSuccess(response)) {
			showToast({
				...toast,
				type: "success",
			});

			await queryClient.invalidateQueries({ queryKey: service.indexArchivedKey });
			await queryClient.invalidateQueries({ queryKey: service.indexKey });
			await queryClient.resetQueries({ queryKey: service.optionsKey });
			await queryClient.invalidateQueries({ queryKey: service.getTenantsKey(id) });
			await queryClient.invalidateQueries({ queryKey: service.getArchivedTenantsKey(id) });
			await queryClient.invalidateQueries({ queryKey: contactUpdateInquiryService.indexKey });
			await queryClient.invalidateQueries({ queryKey: contactFilterService.indexKey });
			return true;
		}
		return false;
	};
};
